import { ArrowRight, Lock } from "lucide-react"
import { Modal } from "@/componentes/ui/Modal"

import { formatearImporte } from "./PanelRubros"

/**
 * Modal de cambio de divisa entre cuentas propias.
 *
 * ⚠ **Enseña la tasa que va a congelar antes de escribir nada, y no deja teclearla.**
 * La tasa sale del ledger inmutable del paso 2: dejarla editable permitiría registrar
 * una conversión a un precio que nunca existió, y como el ledger es append-only, esa
 * cifra quedaría para siempre.
 *
 * ⚠ **Nada se escribe hasta confirmar.** Convertir divisa a la tasa del día fija el
 * costo en bolívares de una operación: es una decisión de tesorería, no un detalle que
 * el sistema pueda resolver solo mientras alguien intenta pagar una factura. Este
 * modal existe justamente para que esa decisión la tome una persona.
 */

export type Propuesta = {
  cuentaOrigen: string
  codigoOrigen: "USD" | "BS"
  codigoDestino: "USD" | "BS"
  tasa: string
  montoOrigen: string
  montoDestino: string
}

type Props = {
  propuesta: Propuesta
  onConfirmar: () => void
  onCerrar: () => void
}

export function ModalCambioDivisa({ propuesta, onConfirmar, onCerrar }: Props) {
  return (
    // ⚠ **Un `Modal` de verdad (12/10/2026).** Era un panel dentro de la página que
    // se anunciaba `aria-modal` sin serlo: sin capa, sin Escape y sin foco, en una
    // pantalla de dinero. Escape y la X equivalen a cancelar.
    <Modal
      abierto
      titulo={"Convertir divisa para completar el pago"}
      onCerrar={onCerrar}
      ancho="sm"
    >
      <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
        No hay saldo suficiente en {propuesta.codigoDestino}. Se puede tomar de{" "}
        {propuesta.cuentaOrigen}.
      </p>

      <p className="mt-3 flex items-center justify-center gap-3 text-lg">
        <span className="tabular-nums">
          {formatearImporte(propuesta.montoOrigen, propuesta.codigoOrigen, "-")}
        </span>
        <ArrowRight size={18} aria-hidden={true} />
        <span className="tabular-nums">
          {formatearImporte(propuesta.montoDestino, propuesta.codigoDestino, "+")}
        </span>
      </p>

      {/*
        ⚠ Un `<output>` con candado, no un `<input>`. La tasa viene del ledger
        inmutable; permitir teclearla dejaría registrada una conversión a un precio
        que nunca existió.
      */}
      <p className="mt-3 flex items-center justify-center gap-2 text-sm">
        <Lock size={14} aria-hidden={true} />
        <span style={{ color: "var(--fg-muted)" }}>Tasa que se congela:</span>
        <output data-testid="tasa-congelada" className="font-medium tabular-nums">
          {propuesta.tasa}
        </output>
      </p>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-[var(--radius-control)] border px-3 py-1.5"
          style={{ borderColor: "var(--border)" }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirmar}
          className="rounded-[var(--radius-control)] px-3 py-1.5"
          style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
        >
          Confirmar conversión
        </button>
      </div>
    </Modal>
  )
}
